<template>
  <v-dialog v-model="internalValue" max-width="800px" persistent>
    <v-card>
      <v-card-title>
        <v-icon left color="green">mdi-check-circle</v-icon>
        {{ $t('onboarding.convert-to-organization') }}
      </v-card-title>

      <v-card-text>
        <v-alert v-if="error" type="error" dense outlined class="mb-4">
          {{ error }}
        </v-alert>

        <v-alert type="info" dense outlined class="mb-4">
          <div class="mb-2">
            <strong>{{ $t('onboarding.conversion-info.title') }}</strong>
          </div>
          <div class="text-caption">
            {{ $t('onboarding.conversion-info.description-auto') }}
          </div>
        </v-alert>

        <v-form ref="form" v-model="valid">
          <!-- Parent Organization (Required) -->
          <organization-select
            v-model="form.parentId"
            :label="$t('onboarding.conversion.parent-organization') + ' *'"
            :rules="[v => !!v || $t('rules.field-required')]"
            outlined
            clearable
          />
        </v-form>

        <v-divider class="my-4"></v-divider>

        <!-- Recap of data that will be used -->
        <div class="text-subtitle-2 mb-3">{{ $t('onboarding.conversion.data-recap') }}</div>
        <div class="text-caption mb-2 text--secondary">
          {{ $t('onboarding.conversion.data-recap-hint') }}
        </div>

        <v-simple-table dense>
          <tbody>
            <tr>
              <td class="text--secondary"><strong>{{ $t('onboarding.conversion.invoice-contact-name') }}</strong></td>
              <td>{{ enrollment.companyName || '-' }}</td>
            </tr>
            <tr>
              <td class="text--secondary"><strong>{{ $t('onboarding.conversion.invoice-contact-email') }}</strong></td>
              <td>{{ enrollment.email || '-' }}</td>
            </tr>
            <tr>
              <td class="text--secondary"><strong>{{ $t('onboarding.conversion.invoice-contact-phone') }}</strong></td>
              <td>{{ enrollment.phone || '-' }}</td>
            </tr>
            <tr>
              <td class="text--secondary"><strong>{{ $t('onboarding.conversion.invoice-contact-address') }}</strong></td>
              <td>{{ formatAddress(enrollment.address) }}</td>
            </tr>
            <tr v-if="enrollment.siret">
              <td class="text--secondary"><strong>{{ $t('siret') }}</strong></td>
              <td>{{ enrollment.siret }}</td>
            </tr>
            <tr v-if="enrollment.vatNumber">
              <td class="text--secondary"><strong>{{ $t('vat-number') }}</strong></td>
              <td>{{ enrollment.vatNumber }}</td>
            </tr>
            <tr v-if="enrollment.evcoId">
              <td class="text--secondary"><strong>{{ $t('evco-id') }}</strong></td>
              <td>{{ enrollment.evcoId }}</td>
            </tr>
            <tr>
              <td class="text--secondary"><strong>{{ $t('language') }}</strong></td>
              <td>{{ formatLocale(enrollment.locale) }}</td>
            </tr>
            <tr>
              <td class="text--secondary"><strong>{{ $t('country') }}</strong></td>
              <td>{{ enrollment.address?.country || 'FR' }}</td>
            </tr>
            <tr>
              <td class="text--secondary"><strong>{{ $t('currency') }}</strong></td>
              <td>EUR</td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text small @click="close" :disabled="loading">
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          color="green"
          small
          :loading="loading"
          :disabled="!valid"
          @click="convert"
        >
          <v-icon left small>mdi-check-circle</v-icon>
          {{ $t('onboarding.confirm-convert-button') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import PaymentOnboardingRepository from '@repository/PaymentOnboardingRepository'
import OrganizationSelect from '@/components/blocks/Select/OrganizationSelect.vue'
import CommonMixin from '@/components/mixins/CommonMixin'

export default {
  name: 'ConvertToOrganizationDialog',

  mixins: [CommonMixin],

  components: {
    OrganizationSelect
  },

  props: {
    value: {
      type: Boolean,
      default: false
    },
    enrollment: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      valid: false,
      loading: false,
      error: null,
      form: {
        parentId: null
      }
    }
  },

  computed: {
    internalValue: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    }
  },

  watch: {
    value(val) {
      if (!val) {
        this.resetForm()
      }
    }
  },

  methods: {

    resetForm() {
      this.form = {
        parentId: null
      }
      this.error = null
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },

    async convert() {
      if (!this.$refs.form.validate()) return

      this.loading = true
      this.error = null

      try {
        const payload = {
          parentId: this.form.parentId
        }

        const result = await PaymentOnboardingRepository.convertToOrganization(this.enrollment.id, payload)

        this.$dialog?.notify?.success && this.$dialog.notify.success(
          this.$t('onboarding.conversion-success-with-account', { accountNumber: result.accountNumber })
        )

        // Warn if Stripe sync failed
        if (result.stripeSyncError) {
          this.$dialog?.notify?.warning && this.$dialog.notify.warning(
            this.$t('onboarding.stripe-sync-error', { error: result.stripeSyncError }),
            { timeout: 10000 }
          )
        }

        this.$emit('converted', result)
        this.close()
      } catch (err) {
        this.notifyError(err)
        // Also store for display in dialog
        this.error = err.response?.data?.message || err.message
      } finally {
        this.loading = false
      }
    },

    formatAddress(address) {
      if (!address) return '-'
      const parts = []
      if (address.street) parts.push(address.street)
      if (address.zipcode || address.city) {
        const cityPart = [address.zipcode, address.city].filter(Boolean).join(' ')
        parts.push(cityPart)
      }
      if (address.country) parts.push(address.country)
      return parts.join(', ') || '-'
    },

    formatLocale(locale) {
      const locales = {
        fr: 'Français',
        en: 'English',
        nl: 'Nederlands'
      }
      return locales[locale] || locale || 'Français'
    },

    close() {
      this.internalValue = false
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
